import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Table } from 'primeng/table';
import { ConfirmationService } from 'primeng/api';
import { ApiService } from 'src/app/core/services/api.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { Investment, CURRENCY_SYMBOLS, InvestmentCurrency } from 'src/app/core/endpoints/models/investments';
import { Transaction } from 'src/app/core/endpoints/models/transactions';
import * as moment from "moment";

@Component({
  selector: 'app-transactions',
  templateUrl: './transactions.component.html',
  styleUrls: ['./transactions.component.scss']
})
export class TransactionsComponent implements OnInit {
  @ViewChild('dt1') table: Table;
  @ViewChild('filter') filter: ElementRef;

  transactions: Transaction[] = [];
  investment: Investment;
  typeOptions: any[] = [
    { label: 'Depósito', value: 'deposito' },
    { label: 'Retirada', value: 'retirada' }
  ];
  
  loading: boolean = false;
  dialogTransaction: boolean = false;
  mode: 'register' | 'edit' = 'register';
  transactionSelected: Transaction;

  form: FormGroup = this.fb.group({
    investment_id: ['', Validators.required],
    transaction_date: ['', Validators.required],
    amount: [
      '',
      [Validators.required, Validators.pattern("^[0-9]+(\\.[0-9]{1,2})?$")]
    ],
    type: ['', Validators.required],
    description: [''],
    status: [1]
  });

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private toast: ToastService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    let uuid = localStorage.getItem('investment');
    if (uuid){
      this.loadInvestment(uuid);
    }
  }

  loadInvestment(uuid:any) {
    this.loading = true;
    this.apiService.getEntity('investments',uuid).subscribe({
      next: (result) => {
        this.investment = result.investments;
        this.loadTransactions();
      },
      error: (err) => {
        console.error(err);
        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  loadTransactions() {
    let uuid :any = localStorage.getItem('investments');
    this.apiService.getEntity('transactions/'+this.investment.id).subscribe({
      next: (result) => {
        this.transactions = result.transactions;
      },
      error: (err) => {
        console.error(err);
      }
    });
  }

  clear(table: Table) {
    table.clear();
    this.filter.nativeElement.value = '';
  }

  showDialog(transaction?: Transaction) {
    this.form.patchValue({
      investment_id: this.investment.id,
    });
    this.transactionSelected = undefined;
    this.resetForm();
    if (transaction) {
      this.transactionSelected = transaction;
      this.form.patchValue({
        transaction_date: moment(transaction.transaction_date+' 12:00:00').format('DD-MM-YYYY'),
        amount: transaction.amount,
        type: transaction.type,
        description: transaction.description
      });
      this.mode = 'edit';
    } else {
      this.mode = 'register';
    }
    this.dialogTransaction = true;
  }

  registerTransaction() {
    const formValue = this.form.value;
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      return this.toast.show('Aviso', 'Revise los datos del formulario', 'warn');
    }
    var transactionDate:any;
    if (typeof formValue.transaction_date === 'object') {
		transactionDate = moment(formValue.transaction_date).format('YYYY-MM-DD');
    } else {
		transactionDate = moment(formValue.transaction_date, 'DD-MM-YYYY').format('YYYY-MM-DD');
    }
    
    if (moment(transactionDate).isBefore(this.investment.start_date)) {
      this.loading = false;
      return this.toast.show('Aviso', 'La fecha de transacción es menor a la fecha de inicio de inversión.', 'warn');
    } 

    if (moment(transactionDate).isAfter(this.investment.end_date)) {
      this.loading = false;
      return this.toast.show('Aviso', 'La fecha de transacción es mayor a la fecha de fin de inversión.', 'warn');
    } 

    this.form.patchValue({ transaction_date: transactionDate });
    this.loading = true;
    this.apiService.addEntity('registerTransaction', this.form.value).subscribe({
      next: (result) => {
        this.dialogTransaction = false;
        this.toast.show('Transacción creada correctamente');
        this.loadTransactions();
        this.resetForm();
      },
      error: (err) => {
        let message = 'Ocurrió un error al crear la transacción, inténtelo de nuevo';
        if (err && err.error && err.error.errors) {
          message = '';
          for (const field in err.error.errors) {
            err.error.errors[field].forEach((errorMsg: string) => {
              message += `${errorMsg}\n`;
            });
          }
        }
        this.toast.show('Error', message, 'error');
        this.loading = false;
        let transactionDate = moment(formValue.transaction_date, 'YYYY-MM-DD').format('DD-MM-YYYY');
        this.form.patchValue({ transaction_date: transactionDate });
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  updateTransaction() {
    const formValue = this.form.value;
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      return this.toast.show('Aviso', 'Revise los datos del formulario', 'warn');
    }
    
    var transactionDate:any;
    if (typeof formValue.transaction_date === 'object') {
		transactionDate = moment(formValue.transaction_date).format('YYYY-MM-DD');
    } else {
		transactionDate = moment(formValue.transaction_date, 'DD-MM-YYYY').format('YYYY-MM-DD');
    }
    
    if (moment(transactionDate).isBefore(this.investment.start_date)) {
      this.loading = false;
      return this.toast.show('Aviso', 'La fecha de transacción es menor a la fecha de inicio de inversión.', 'warn');
    } 

    if (moment(transactionDate).isAfter(this.investment.end_date)) {
      this.loading = false;
      return this.toast.show('Aviso', 'La fecha de transacción es mayor a la fecha de fin de inversión.', 'warn');
    } 
    this.loading = true;
    this.form.patchValue({ transaction_date: transactionDate });
    const payload = { id: this.transactionSelected.id, ...this.form.value };
    this.apiService.updateEntity('updateTransaction', this.transactionSelected.id, payload).subscribe({
      next: (result) => {
        this.toast.show('Transacción actualizada correctamente');
        this.dialogTransaction = false;
        this.loadTransactions();
        this.resetForm();
      },
      error: (err) => {
        let message = 'Ocurrió un error al actualizar la transacción, inténtelo de nuevo';
        if (err && err.error && err.error.errors) {
          message = '';
          for (const field in err.error.errors) {
            err.error.errors[field].forEach((errorMsg: string) => {
              message += `${errorMsg}\n`;
            });
          }
        }
        this.toast.show('Error', message, 'error');
        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  deleteTransaction(transaction: Transaction) {
    if (this.loading) return;
    this.confirmationService.confirm({
      message: '¿Está seguro que desea eliminar la transacción?',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.loading = true;
        this.apiService.deleteEntity('deleteTransaction', transaction.id).subscribe({
          next: () => {
            this.toast.show('Transacción eliminada correctamente');
            this.loadTransactions();
          },
          error: () => {
            this.toast.show('Error', 'Ocurrió un error eliminando la transacción, inténtelo de nuevo', 'error');
          },
          complete: () => {
            this.loading = false;
          }
        });
      }
    });
  }

  resetForm() {
    this.form.reset();
    this.form.patchValue({
      investment_id: this.investment.id,
    });
  }
  
  formatNumber(num: number): string {
    const fixedNum = num.toFixed(2);
    const [integerPart, decimalPart] = fixedNum.split('.');
    const withThousands = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    if(parseFloat(decimalPart) === 0){
      return withThousands;
    }
    return `${withThousands},${decimalPart}`;
  }

  getCurrencySymbol(currency?: InvestmentCurrency): string {
    return CURRENCY_SYMBOLS[currency] || CURRENCY_SYMBOLS['EUR'];
  }
}
